import React, {useState, useEffect, useMemo} from 'react';
import {
  Formik, Form, useFormikContext, useField,
} from 'formik';
import * as yup from 'yup';
import {TrashIcon} from '@heroicons/react/outline';

import StepNavigation from '../StepNavigation';
import {StyledTextArea} from '../../../components/Forms';
import {ChevronIcon} from '../../../components/svg';
import TextAreaWithRelatedQuestions from '../TextAreaWithRelatedQuestions';
import {STAGES} from '../../../../../lib/utils';
import {cx} from '../../../util';

const MAX_QUESTIONS_PER_STAGE = 10;

// Create a lookup map from STAGES
const STAGES_MAP = STAGES.reduce((acc, stage) => {
  acc[stage.id] = stage;
  return acc;
}, {});

const createSchema = (selectedStages) => yup.object({
  questionsByStage: yup.object().test(
    'min-one-question-per-stage',
    function validate(value) {
      if (!value) return this.createError({message: 'Questions are required'});

      const selectedSet = new Set(selectedStages);
      const missingStages = STAGES
        .filter((stage) => selectedSet.has(stage.id) && stage.id !== 'recruiter') // Don't require questions for recruiter stage
        .filter((stage) => {
          const stageQuestions = value[stage.id] || [];
          return !stageQuestions.some((q) => q.text?.trim() || q.existing);
        })
        .map((stage) => stage.label);

      if (missingStages.length > 0) {
        return this.createError({
          message: `Please add at least one question for: ${missingStages.join(', ')}`,
        });
      }
      return true;
    },
  ),
});

const QuestionInput = ({
  name, index, question, onBlur, autoFocus, totalQuestions, onRemove,
}) => {
  const [, , answerHelpers] = useField(`${name}.answer`);
  const [answerField] = useField(`${name}.answer`);
  const [showAnswer, setShowAnswer] = useState(!!answerField.value);
  const [isQuestionHovered, setIsQuestionHovered] = useState(false);
  // Starts hovered when showAnswer flips to true
  const [isAnswerHovered, setIsAnswerHovered] = useState(true);

  const handleClose = () => {
    if (totalQuestions > 1) {
      onRemove(index);
    } else {
      onRemove(index, true); // clear instead of remove
    }
  };

  const isFirst = index === 0;
  const isEmpty = !question.text?.trim();

  return (
    <div className="mb-3">
      <div
        className="-mr-12 pr-12"
        onMouseEnter={() => setIsQuestionHovered(true)}
        onMouseLeave={() => setIsQuestionHovered(false)}
      >
        <div className="flex items-start gap-2 mb-2">
          <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 font-semibold text-sm flex-shrink-0">
            {index + 1}
          </span>
          <div className="flex-1 relative">
            <TextAreaWithRelatedQuestions
              name={name}
              question={question}
              onBlur={onBlur}
              autoFocus={autoFocus}
            />
            {isQuestionHovered && (!isEmpty || !isFirst) && (
              <button
                type="button"
                onClick={handleClose}
                className="absolute -right-10 top-0 flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                aria-label={totalQuestions > 1 ? 'Remove question' : 'Clear question'}
                title={totalQuestions > 1 ? 'Remove question' : 'Clear question'}
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
      {showAnswer ? (
        <div
          className="ml-12 -mr-12 pr-12"
          onMouseEnter={() => setIsAnswerHovered(true)}
          onMouseLeave={() => setIsAnswerHovered(false)}
        >
          <div className="relative">
            <StyledTextArea
              value={answerField.value}
              name={`${name}.answer`}
              placeholder="Add your answer"
              onChange={answerField.onChange}
              onBlur={onBlur}
              className="resize-vertical min-h-[100px]"
            />
            {isAnswerHovered && (
              <button
                type="button"
                onClick={() => { answerHelpers.setValue(''); setShowAnswer(false); }}
                className="absolute -right-10 top-0 flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                aria-label="Clear answer"
                title="Clear answer"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="text-right">
          <button
            type="button"
            className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
            onClick={() => {
              setShowAnswer(true);
              answerHelpers.setValue('');
            }}
          >
            + Add your answer
          </button>
        </div>
      )}
    </div>
  );
};

const StageSection = ({
  stageId, maxQuestions, onEdit, expanded, onToggle,
}) => {
  const {values, setFieldValue} = useFormikContext();
  const stageQuestions = values.questionsByStage[stageId] || [];
  const stage = STAGES_MAP[stageId];
  const StageIcon = stage.icon;
  const [focusIndex, setFocusIndex] = useState(null);

  // Clear focusIndex once focus is applied, so that it doesn't keep stealing focus
  useEffect(() => {
    if (focusIndex !== null) {
      setFocusIndex(null);
    }
  }, [focusIndex]);

  const handleBlur = () => {
    if (onEdit) onEdit({questionsByStage: values.questionsByStage});
  };

  const addQuestion = () => {
    const newIndex = stageQuestions.length;
    const updated = {
      ...values.questionsByStage,
      [stageId]: [...stageQuestions, {text: '', answer: ''}],
    };
    setFieldValue('questionsByStage', updated);
    setFocusIndex(newIndex);
    if (onEdit) onEdit({questionsByStage: updated});
  };

  const removeQuestion = (index, clearOnly = false) => {
    let updated;
    if (clearOnly) {
      // Clear the content instead of removing
      const updatedQuestions = [...stageQuestions];
      updatedQuestions[index] = {text: '', answer: ''};
      updated = {
        ...values.questionsByStage,
        [stageId]: updatedQuestions,
      };
    } else {
      // Remove the question
      updated = {
        ...values.questionsByStage,
        [stageId]: stageQuestions.filter((_, i) => i !== index),
      };
    }
    setFieldValue('questionsByStage', updated);
    if (onEdit) onEdit({questionsByStage: updated});
  };

  return (
    <div className="rounded-xl">
      <button
        type="button"
        onClick={onToggle}
        className={cx(
          'w-full flex items-center justify-between px-4 py-3 text-left rounded-xl transition-colors',
          expanded ? 'bg-indigo-50' : 'bg-gray-100 hover:bg-gray-200',
        )}
      >
        <div className="flex items-center gap-2">
          {StageIcon && (
            <StageIcon
              className={cx(
                'h-4 w-4',
                expanded ? 'text-indigo-600' : 'text-gray-500',
              )}
            />
          )}
          <span className={cx(
            'font-semibold',
            expanded ? 'text-indigo-600' : 'text-gray-800',
          )}
          >
            {stage.label}
          </span>
        </div>
        <ChevronIcon className={cx('h-5 w-5', expanded ? 'text-indigo-600' : 'text-gray-400')} />
      </button>
      {expanded && (
        <div className="py-2">
          {stageQuestions.map((q, i) => (
            <QuestionInput
              // eslint-disable-next-line react/no-array-index-key
              key={`${stageId}-q-${i}`}
              name={`questionsByStage.${stageId}.${i}`}
              index={i}
              question={q}
              onBlur={handleBlur}
              autoFocus={focusIndex === i}
              totalQuestions={stageQuestions.length}
              onRemove={removeQuestion}
            />
          ))}
          {stageQuestions.length === 0 && (
            <QuestionInput
              key={`${stageId}-q-0`}
              name={`questionsByStage.${stageId}.0`}
              index={0}
              question={{text: '', answer: ''}}
              onBlur={handleBlur}
              totalQuestions={1}
              onRemove={removeQuestion}
            />
          )}
          {stageQuestions.length > 0 && stageQuestions.length < maxQuestions && (
            <div className="flex items-center gap-2 mb-2">
              <span className="flex items-center justify-center w-10 h-10 rounded-lg border-2 border-dashed border-gray-300 text-gray-400 font-semibold text-sm flex-shrink-0">
                +
              </span>
              <button
                type="button"
                onClick={addQuestion}
                className="flex-1 text-left px-3 py-2 text-gray-400 border border-gray-200 rounded-lg hover:border-gray-300 hover:text-gray-500 transition-colors"
              >
                Add another question
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const QuestionsStep = ({
  draft, onNext, onBack, onEdit,
}) => {
  // Get stages from draft (selected in previous step)
  const stageIds = useMemo(() => (draft.stages || []).map((s) => s.name), [draft.stages]);
  const stages = useMemo(() => new Set(stageIds), [stageIds]);
  const validationSchema = useMemo(() => createSchema(stageIds), [stageIds]);

  // Track which stages are expanded (multiple can be open)
  const [expandedStages, setExpandedStages] = useState(() => {
    const initial = {};

    // Expand stages that have existing questions with content
    (draft.stages || []).forEach((s) => {
      const hasContent = s.questions?.some((q) => q.text?.trim());
      if (hasContent) {
        initial[s.name] = true;
      }
    });

    // If no stages have content, expand the first selected stage
    if (Object.keys(initial).length === 0 && draft.stages?.length) {
      initial[draft.stages[0].name] = true;
    }

    return initial;
  });

  // Hack to keep initialValues stable across renders,
  // preventing enableReinitialize from resetting form values
  const [initialValues] = useState(() => {
    const questionsByStage = {};
    (draft.stages || []).forEach((s) => {
      questionsByStage[s.name] = s.questions?.length > 0
        ? s.questions
        : [{text: '', answer: ''}];
    });
    return {questionsByStage};
  });

  const toggleStage = (stageId) => {
    setExpandedStages((prev) => ({
      ...prev,
      [stageId]: !prev[stageId],
    }));
  };

  const handleEdit = ({questionsByStage: qbs}) => {
    const updatedStages = (draft.stages || []).map((s) => ({
      ...s,
      questions: qbs?.[s.name] || s.questions || [],
    }));
    onEdit?.({stages: updatedStages});
  };

  const handleSubmit = (values) => {
    // Merge updated questions back into stages, filtering out empty questions
    const updatedStages = (draft.stages || []).map((s) => ({
      ...s,
      questions: (values.questionsByStage[s.name] || []).filter((q) => q.text?.trim()),
    }));
    return onNext({stages: updatedStages});
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({errors, submitCount, isSubmitting}) => (
        <Form className="flex flex-col gap-y-3">
          {STAGES
            .filter((stage) => stages.has(stage.id))
            .map((stage) => (
              <StageSection
                key={stage.id}
                stageId={stage.id}
                maxQuestions={MAX_QUESTIONS_PER_STAGE}
                onEdit={handleEdit}
                expanded={!!expandedStages[stage.id]}
                onToggle={() => toggleStage(stage.id)}
              />
            ))}
          {submitCount > 0 && errors.questionsByStage && (
            <p className="text-red-600 text-xs">{errors.questionsByStage}</p>
          )}
          <StepNavigation onBack={onBack} isSubmitting={isSubmitting} />
        </Form>
      )}
    </Formik>
  );
};

export default QuestionsStep;
